import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  ACCOUNT_MANAGEMENT_TITLE,
  STAFF_LIST_TITLE,
  GROUP_MANAGEMENT_TITLE,
} from '../../constants/labels';

interface RouteInfo {
  path: string;
  title: string;
}

interface MetaTitleProps {
  customTitle?: string;
}

export const MetaTitle: React.FC<MetaTitleProps> = ({ customTitle }) => {
  const pathName = useLocation().pathname;
  const subTitle = 'Gen Drive';

  const routes: RouteInfo[] = [
    { path: '/', title: 'ログイン' },
    { path: '/auth/password_reset', title: 'パスワード忘れ' },
    { path: '/auth/password_reset/complete', title: 'パスワード忘れメール送信完了' },
    { path: '/auth/password_change', title: 'パスワード変更' },
    { path: '/auth/password_change/complete', title: 'パスワード変更完了' },
    { path: '/auth/register', title: '会員登録（無料）' },
    { path: '/auth/register/sent', title: '会員登録メール送信完了' },
    { path: '/auth/register/info', title: '会員情報登録' },
    { path: '/auth/register/complete', title: '会員登録完了' },
    { path: '/ai-ocr', title: 'AI読取' },
    { path: `/ai-ocr/result/:id`, title: 'AI読取結果' },
    { path: '/ai-ocr/delete/complete', title: 'AI読取結果削除完了' },
    { path: '/ai-ocr/demo', title: 'AI読取デモ' },
    { path: '/ai-ocr/demo/add', title: 'AI読取デモ新規登録' },
    { path: '/ai-ocr/demo/:id', title: 'AI読取デモ結果' },
    { path: '/ai-ocr/pattern/:patternId', title: '読取パターンAI読取' },
    { path: '/ai-ocr/pattern/:patternId/result/:resultId', title: '読取パターンAI読取結果' },
    { path: '/pattern', title: '読取パターン一覧' },
    { path: '/pattern/add', title: '読取パターン新規作成' },
    { path: '/pattern/:id', title: '読取パターン編集' },
    { path: '/pattern/:patternId/accuracy', title: 'パターン精度確認' },
    { path: '/master', title: 'マスタデータ' },
    { path: '/dictionary', title: '辞書データ管理' },
    { path: '/staff', title: STAFF_LIST_TITLE },
    { path: '/staff/invite', title: '担当者招待' },
    { path: '/staff/invite/complete', title: '担当者招待完了' },
    { path: '/staff/invite/cancel', title: '担当者招待取消完了' },
    { path: '/staff/register', title: '担当者登録' },
    { path: '/staff/register/complete', title: '担当者登録完了' },
    { path: '/staff/edit', title: '担当者編集' },
    { path: '/staff/edit/complete', title: '担当者編集完了' },
    { path: '/staff/delete/complete', title: '担当者削除完了' },
    { path: '/group', title: GROUP_MANAGEMENT_TITLE },
    { path: '/group/register', title: 'グループ作成' },
    { path: '/group/edit', title: 'グループ編集' },
    { path: '/admin/company', title: '企業一覧' },
    { path: '/admin/company/:companyId', title: '企業詳細' },
    { path: '/admin/company/invite', title: '企業招待' },
    { path: '/admin/company/invite/complete', title: '企業招待完了' },
    { path: '/company/edit', title: '会社情報編集' },
    { path: '/company/edit/complete', title: '会社情報編集完了' },
    { path: '/admin/patterns', title: '全ての読取パターン' },
    { path: '/admin/masters', title: '全てのマスタ' },
    { path: '/admin/ocr_credit_add/:companyId', title: 'OCRクレジット追加' },
    { path: '/admin/ocr_credit_add/:companyId/complete', title: 'OCRクレジット追加完了' },
    { path: '/admin/user_limit_change/:companyId', title: '担当者数変更' },
    { path: '/admin/user_limit_change/:companyId/complete', title: '担当者数変更完了' },
    { path: '/admin/plan_change/:companyId', title: '契約プラン変更' },
    { path: '/admin/plan_change/:companyId/complete', title: '契約プラン変更完了' },
    { path: '/company', title: ACCOUNT_MANAGEMENT_TITLE },
    { path: '/contact', title: 'お問い合わせ' },
    { path: '/contact/confirm', title: 'お問い合わせ確認' },
    { path: '/contact/complete', title: '会員問い合わせ完了' },
  ];

  const getPageTitle = (path: string): string => {
    // 完全一致するルートを探す
    const exactRoute = routes.find(r => r.path === path);
    if (exactRoute) return exactRoute.title;

    // 完全一致しない場合、パラメータを含むルートを探す
    const paramRoute = routes.find(r => {
      const routeParts = r.path.split('/');
      const pathParts = path.split('/');

      if (routeParts.length !== pathParts.length) return false;

      return routeParts.every((part, index) => part.startsWith(':') || part === pathParts[index]);
    });

    return paramRoute ? paramRoute.title : 'ページが見つかりません';
  };

  const finalTitle = customTitle || getPageTitle(pathName);

  return (
    <Helmet>
      <title>
        {finalTitle} | {subTitle}
      </title>
      <meta property="og:title" content={`${finalTitle} | ${subTitle}`} />
      <meta property="og:description" content="Gen Drive" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${window.location.origin}/ogp.png`} />
    </Helmet>
  );
};

export default MetaTitle;
